import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import Button from "../Button/Button";
import {
  MONTHS,
  WEEKDAYS,
  buildCalendarGrid,
  formatDate,
  isSameDate,
  isToday,
  parseDate,
  type CalendarDay,
} from "../DatePicker/calendar-utils";
import Icon from "../Icon/Icon";
import TimeColumn from "../TimePicker/TimeColumn";
import {
  HOURS,
  MINUTES,
  formatTime,
  parseTime,
} from "../TimePicker/time-utils";
import styles from "./DateTimePicker.module.css";

type DateTimePickerProps = {
  /** Current value in "YYYY-MM-DD HH:mm" format */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
};

type ViewMode = "calendar" | "months" | "years";

function parseDateTime(value?: string): {
  year: number;
  month: number;
  day: number;
  hours: string;
  minutes: string;
} {
  if (!value) {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
      hours: "00",
      minutes: "00",
    };
  }
  const [datePart, timePart] = value.split(" ");
  const { year, month, day } = parseDate(datePart);
  const { hours, minutes } = parseTime(timePart);
  return { year, month, day, hours, minutes };
}

function formatDateTime(
  year: number,
  month: number,
  day: number,
  hours: string,
  minutes: string
): string {
  return `${formatDate(year, month, day)} ${formatTime(hours, minutes)}`;
}

const timeColumnClassNames = {
  column: styles.column,
  item: styles.item,
  itemSelected: styles.itemSelected,
};

function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date & time",
  disabled = false,
  error = false,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const init = parseDateTime(value);
  const todayYear = new Date().getFullYear();

  // Calendar state
  const [viewYear, setViewYear] = useState(init.year);
  const [viewMonth, setViewMonth] = useState(init.month);
  const [selectedYear, setSelectedYear] = useState(init.year);
  const [selectedMonth, setSelectedMonth] = useState(init.month);
  const [selectedDay, setSelectedDay] = useState(init.day);
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");

  // Time state
  const [selectedHours, setSelectedHours] = useState(init.hours);
  const [selectedMinutes, setSelectedMinutes] = useState(init.minutes);

  // Dropdown placement
  const [dropdownPlacement, setDropdownPlacement] = useState<"bottom" | "top">(
    "bottom"
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const yearGridRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current || !containerRef.current) return;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    if (spaceBelow < dropdownHeight + 4 && spaceAbove > spaceBelow) {
      setDropdownPlacement("top");
    } else {
      setDropdownPlacement("bottom");
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (viewMode === "years" && yearGridRef.current) {
      const el = yearGridRef.current.querySelector(
        `[data-year="${viewYear}"]`
      ) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: "center" });
      }
    }
  }, [viewMode, viewYear]);

  // Sync from external value
  useEffect(() => {
    const parsed = parseDateTime(value);
    setSelectedYear(parsed.year);
    setSelectedMonth(parsed.month);
    setSelectedDay(parsed.day);
    setSelectedHours(parsed.hours);
    setSelectedMinutes(parsed.minutes);
  }, [value]);

  // Click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setViewMode("calendar");
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    if (!disabled) {
      const parsed = parseDateTime(value);
      setSelectedYear(parsed.year);
      setSelectedMonth(parsed.month);
      setSelectedDay(parsed.day);
      setSelectedHours(parsed.hours);
      setSelectedMinutes(parsed.minutes);
      setViewYear(parsed.year);
      setViewMonth(parsed.month);
      setViewMode("calendar");
      setIsOpen(true);
    }
  }, [disabled, value]);

  const handleCancel = useCallback(() => {
    const parsed = parseDateTime(value);
    setSelectedYear(parsed.year);
    setSelectedMonth(parsed.month);
    setSelectedDay(parsed.day);
    setSelectedHours(parsed.hours);
    setSelectedMinutes(parsed.minutes);
    setIsOpen(false);
    setViewMode("calendar");
  }, [value]);

  const handleSelect = useCallback(() => {
    onChange?.(
      formatDateTime(
        selectedYear,
        selectedMonth,
        selectedDay,
        selectedHours,
        selectedMinutes
      )
    );
    setIsOpen(false);
    setViewMode("calendar");
  }, [
    onChange,
    selectedYear,
    selectedMonth,
    selectedDay,
    selectedHours,
    selectedMinutes,
  ]);

  const handleNow = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const h = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    setSelectedYear(y);
    setSelectedMonth(m);
    setSelectedDay(d);
    setSelectedHours(h);
    setSelectedMinutes(min);
    setViewYear(y);
    setViewMonth(m);
  }, []);

  const handlePrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const handleDayClick = useCallback((day: CalendarDay) => {
    setSelectedYear(day.year);
    setSelectedMonth(day.month);
    setSelectedDay(day.day);
    if (!day.isCurrentMonth) {
      setViewYear(day.year);
      setViewMonth(day.month);
    }
  }, []);

  const handleMonthSelect = useCallback((monthIndex: number) => {
    setViewMonth(monthIndex);
    setViewMode("calendar");
  }, []);

  const handleYearSelect = useCallback((year: number) => {
    setViewYear(year);
    setViewMode("months");
  }, []);

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleOpen();
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    },
    [handleOpen]
  );

  const displayValue = value
    ? (() => {
        const parsed = parseDateTime(value);
        return `${String(parsed.day).padStart(2, "0")} ${MONTHS[parsed.month]?.slice(0, 3)} ${parsed.year} ${parsed.hours}:${parsed.minutes}`;
      })()
    : undefined;

  const weeks = buildCalendarGrid(viewYear, viewMonth);

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger + (error ? ` ${styles.error}` : "")}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Icon name="Calendar" className={styles.triggerIcon} />
        <span
          className={
            displayValue ? styles.triggerValue : styles.triggerPlaceholder
          }
        >
          {displayValue ?? placeholder}
        </span>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`${styles.dropdown} ${dropdownPlacement === "top" ? styles.dropdownTop : ""}`}
          role="dialog"
          aria-label="Date and time picker"
        >
          <div className={styles.content}>
            {/* ─── Calendar section (left) ─── */}
            <div className={styles.calendarSection}>
              <div className={styles.header}>
                {viewMode !== "calendar" ? (
                  <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => setViewMode("calendar")}
                  >
                    <Icon name="Left" className={styles.navIcon} />
                    Back
                  </button>
                ) : (
                  <>
                    <div className={styles.headerLabels}>
                      <button
                        type="button"
                        className={styles.headerButton}
                        onClick={() => setViewMode("years")}
                      >
                        {viewYear}
                        <Icon
                          name="Caret_down"
                          className={styles.headerChevron}
                        />
                      </button>
                      <button
                        type="button"
                        className={styles.headerButton}
                        onClick={() => setViewMode("months")}
                      >
                        {MONTHS[viewMonth]}
                        <Icon
                          name="Caret_down"
                          className={styles.headerChevron}
                        />
                      </button>
                    </div>
                    <div className={styles.headerControls}>
                      <button
                        type="button"
                        className={styles.navButton}
                        onClick={handlePrevMonth}
                        aria-label="Previous month"
                      >
                        <Icon name="Left" className={styles.navIcon} />
                      </button>
                      <button
                        type="button"
                        className={styles.navButton}
                        onClick={handleNextMonth}
                        aria-label="Next month"
                      >
                        <Icon name="Right" className={styles.navIcon} />
                      </button>
                    </div>
                  </>
                )}
              </div>

              {viewMode === "calendar" && (
                <div className={styles.calendar}>
                  <div className={styles.weekdayRow}>
                    {WEEKDAYS.map((wd) => (
                      <div key={wd} className={styles.weekday}>
                        {wd}
                      </div>
                    ))}
                  </div>
                  {weeks.map((week, wi) => (
                    <div key={wi} className={styles.weekRow}>
                      {week.map((d, di) => {
                        const selected = isSameDate(
                          d,
                          selectedYear,
                          selectedMonth,
                          selectedDay
                        );
                        const today = isToday(d);
                        return (
                          <button
                            key={di}
                            type="button"
                            className={[
                              styles.day,
                              !d.isCurrentMonth ? styles.dayOutside : "",
                              selected ? styles.daySelected : "",
                              today ? styles.dayToday : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => handleDayClick(d)}
                          >
                            {d.day}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {viewMode === "months" && (
                <div className={styles.monthGrid}>
                  {MONTHS.map((m, i) => (
                    <button
                      key={m}
                      type="button"
                      className={`${styles.monthCell} ${i === viewMonth ? styles.monthCellSelected : ""}`}
                      onClick={() => handleMonthSelect(i)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}

              {viewMode === "years" && (
                <div className={styles.yearGrid} ref={yearGridRef}>
                  {Array.from(
                    { length: 101 },
                    (_, i) => todayYear - 80 + i
                  ).map((y) => (
                    <button
                      key={y}
                      type="button"
                      data-year={y}
                      className={[
                        styles.yearCell,
                        y === viewYear ? styles.yearCellSelected : "",
                        y === todayYear ? styles.yearCellCurrent : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleYearSelect(y)}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Divider ─── */}
            <div className={styles.divider} />

            {/* ─── Time section (right) ─── */}
            <div className={styles.timeSection}>
              <div className={styles.selectors}>
                <TimeColumn
                  items={HOURS}
                  selectedValue={selectedHours}
                  onSelect={setSelectedHours}
                  label="Hours"
                  classNames={timeColumnClassNames}
                />
                <span className={styles.separator}>:</span>
                <TimeColumn
                  items={MINUTES}
                  selectedValue={selectedMinutes}
                  onSelect={setSelectedMinutes}
                  label="Minutes"
                  classNames={timeColumnClassNames}
                />
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.nowAction}>
              <Button
                variant="secondary"
                size="small"
                text="Now"
                icon="Time_and_date"
                onClick={handleNow}
              />
            </div>
            <div className={styles.actions}>
              <Button
                variant="ghost"
                size="small"
                text="Cancel"
                onClick={handleCancel}
              />
              <Button
                variant="primary"
                size="small"
                text="Select"
                onClick={handleSelect}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateTimePicker;
